/**
 * Formats a delivery timestamp to show just the hour in the user's timezone and time format.
 * e.g. "8 AM" (12h) or "08:00" (24h)
 */
/**
 * Formats a generation_hour (0-23) into a readable string like "7 AM UTC".
 */
export function formatGenerationHour(hour: number): string {
  const period = hour >= 12 ? "PM" : "AM";
  const h = hour % 12 || 12;
  return `${h} ${period} UTC`;
}

/**
 * Converts a UTC hour (0-23) to the equivalent hour in the given timezone,
 * and returns the timezone abbreviation label.
 */
export function convertHourToTimezone(
  utcHour: number,
  timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): { hour: number; tzLabel: string } {
  // Build a date at the given UTC hour today
  const now = new Date();
  const utcDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), utcHour, 0, 0));

  // Get the local hour in the target timezone
  const localHour = parseInt(
    new Intl.DateTimeFormat("en-US", { timeZone: timezone, hour: "numeric", hour12: false }).format(utcDate),
    10
  );

  // Get the timezone abbreviation
  const tzLabel = new Intl.DateTimeFormat("en-US", { timeZone: timezone, timeZoneName: "short" })
    .formatToParts(utcDate)
    .find(p => p.type === "timeZoneName")?.value || timezone;

  return { hour: localHour === 24 ? 0 : localHour, tzLabel };
}

/**
 * Formats a UTC generation_hour as a local time string for the given timezone.
 * e.g. "8 AM EST"
 */
export function formatGenerationHourLocal(
  utcHour: number,
  timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): string {
  const { hour, tzLabel } = convertHourToTimezone(utcHour, timezone);
  const period = hour >= 12 ? "PM" : "AM";
  const h = hour % 12 || 12;
  return `${h} ${period} ${tzLabel}`;
}

/**
 * Formats a user's preferred delivery hour (0-23) in their timezone and time format.
 * e.g. "8 AM EST" (12h) or "08:00 EST" (24h)
 */
export function formatUserDeliveryHour(
  hour: number,
  timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
  timeFormat: string = "24h"
): string {
  // delivery_time is the user's preferred local hour, not UTC — format directly
  const tzLabel = new Intl.DateTimeFormat("en-US", { timeZone: timezone, timeZoneName: "short" })
    .formatToParts(new Date())
    .find(p => p.type === "timeZoneName")?.value || timezone;

  if (timeFormat === "12h") {
    const period = hour >= 12 ? "PM" : "AM";
    const h = hour % 12 || 12;
    return `${h} ${period} ${tzLabel}`;
  }
  return `${String(hour).padStart(2, "0")}:00 ${tzLabel}`;
}

export function formatDeliveryTime(
  deliveredAt: string,
  timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
  timeFormat: string = "24h"
): string {
  const date = new Date(deliveredAt);
  if (timeFormat === "12h") {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      timeZone: timezone,
      hour12: true,
      timeZoneName: "short",
    });
  }
  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone,
    hour12: false,
    timeZoneName: "short",
  });
}
